import React, { useState, useRef } from "react";
import { entities, uploadFile, invokeKanbanConfig } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Calendar,
  Clock,
  Paperclip,
  Plus,
  X,
  Download,
  Eye,
  CheckCircle2,
  AlertCircle,
  Printer,
  ArrowRight
} from "lucide-react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from "date-fns";
import { formatarDataAbertura } from "@/lib/formatarDataAbertura";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import FileViewer from "../components/shared/FileViewer";
import { Alert, AlertDescription } from "@/components/ui/alert";

/** Anexos na raiz — mesma fonte que a aba Anexos em Resultados (`AnexosEdital`). */
function getAnexosRaizEdital(edital) {
  if (!edital) return [];
  const a = edital.anexos_adicionais ?? edital.anexosAdicionais;
  return Array.isArray(a) ? a : [];
}

function getPastasAnexosEdital(edital) {
  if (!edital) return [];
  const p = edital.pastas_anexos ?? edital.pastasAnexos;
  return Array.isArray(p) ? p : [];
}

/** Total na área de anexos gerenciada: raiz + arquivos dentro de pastas (sem `arquivos_relacionados` do upload). */
function contarAnexosAreaEdital(edital) {
  const nRaiz = getAnexosRaizEdital(edital).length;
  const nPastas = getPastasAnexosEdital(edital).reduce(
    (acc, pasta) => acc + (Array.isArray(pasta?.anexos) ? pasta.anexos.length : 0),
    0
  );
  return nRaiz + nPastas;
}

export default function StatusReportPage() {
  const [colunasKanban, setColunasKanban] = useState([]);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const reportRef = useRef(null);

  React.useEffect(() => {
    loadColunas();
  }, []);

  const loadColunas = async () => {
    try {
      // Buscar configuração ativa compartilhada na entidade KanbanConfig
      const configs = await entities.KanbanConfig.list();
      const configAtiva = configs.find(c => c.ativo === true);
      
      if (configAtiva && configAtiva.colunas && Array.isArray(configAtiva.colunas) && configAtiva.colunas.length > 0) {
        // Converter cor simples para cor completa com border e text
        const colunasComCoresCompletas = configAtiva.colunas.map(col => {
          let corCompleta = col.cor;
          // Se a cor não tem border/text, adicionar
          if (col.cor && !col.cor.includes('border-')) {
            const corBase = col.cor.replace('bg-', '');
            const corNome = corBase.replace('-100', '');
            corCompleta = `${col.cor} border-${corNome}-300 text-${corNome}-900`;
          }
          return { ...col, cor: corCompleta };
        });
        setColunasKanban(colunasComCoresCompletas);
      } else {
        // Fallback para colunas padrão
        setColunasKanban([
          { id: "analise_edital", titulo: "Análise do Edital", cor: "bg-blue-100 border-blue-300 text-blue-900" },
          { id: "aprovacao_analise", titulo: "Aprovação da Análise", cor: "bg-purple-100 border-purple-300 text-purple-900" },
          { id: "pregao", titulo: "Pregão", cor: "bg-amber-100 border-amber-300 text-amber-900" },
          { id: "suspenso", titulo: "Suspenso", cor: "bg-red-100 border-red-300 text-red-900" },
          { id: "acompanhamento", titulo: "Acompanhamento", cor: "bg-green-100 border-green-300 text-green-900" },
          { id: "encerrado", titulo: "Encerrado", cor: "bg-slate-100 border-slate-300 text-slate-900" }
        ]);
      }
    } catch (error) {
      console.error("Erro ao carregar colunas:", error);
      setColunasKanban([
        { id: "analise_edital", titulo: "Análise do Edital", cor: "bg-blue-100 border-blue-300 text-blue-900" },
        { id: "aprovacao_analise", titulo: "Aprovação da Análise", cor: "bg-purple-100 border-purple-300 text-purple-900" },
        { id: "pregao", titulo: "Pregão", cor: "bg-amber-100 border-amber-300 text-amber-900" },
        { id: "suspenso", titulo: "Suspenso", cor: "bg-red-100 border-red-300 text-red-900" },
        { id: "acompanhamento", titulo: "Acompanhamento", cor: "bg-green-100 border-green-300 text-green-900" },
        { id: "encerrado", titulo: "Encerrado", cor: "bg-slate-100 border-slate-300 text-slate-900" }
      ]);
    }
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editalSelecionado, setEditalSelecionado] = useState(null);
  const [dialogAnexoAberto, setDialogAnexoAberto] = useState(false);
  const [dialogAcaoAberto, setDialogAcaoAberto] = useState(false);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const [novaAcao, setNovaAcao] = useState("");

  const { data: editais = [], isLoading, error } = useQuery({
    queryKey: ['editais-status-report'],
    queryFn: async () => {
      try {
        const data = await entities.Edital.list('-createdAt');
        return (Array.isArray(data) ? data : []).filter(e => 
          e.status === 'concluido' && 
          e.status_aprovacao === 'aprovar_seguir' &&
          !e.oculto_kanban
        );
      } catch (error) {
        console.error("Erro ao carregar editais:", error);
        return [];
      }
    },
    retry: 2,
    staleTime: 30000
  });

  const { data: propostas = [] } = useQuery({
    queryKey: ['propostas-status-report'],
    queryFn: () => entities.Proposta.list()
  });

  const addAnexoMutation = useMutation({
    mutationFn: async ({ editalId, anexo }) => {
      const lista = queryClient.getQueryData(['editais-status-report']) || [];
      const edital = lista.find((e) => e.id === editalId);
      if (!edital) throw new Error('Edital não encontrado');
      const anexosAtuais = getAnexosRaizEdital(edital);
      const novosAnexos = [...anexosAtuais, anexo];

      await entities.Edital.update(editalId, {
        anexos_adicionais: novosAnexos
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editais-status-report'] });
      setDialogAnexoAberto(false);
      setEditalSelecionado(null);
    }
  });

  const addAcaoMutation = useMutation({
    mutationFn: async ({ editalId, acao }) => {
      const edital = editais.find(e => e.id === editalId);
      const acoesAtuais = edital.acoes || [];
      const novasAcoes = [...acoesAtuais, acao];
      
      await entities.Edital.update(editalId, {
        acoes: novasAcoes
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editais-status-report'] });
      setDialogAcaoAberto(false);
      setEditalSelecionado(null);
      setNovaAcao("");
    }
  });

  const handleUploadAnexo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAnexo(true);
    try {
      const { file_url } = await uploadFile(file);
      
      const novoAnexo = {
        url: file_url,
        nome: file.name,
        data_adicao: new Date().toISOString()
      };
      
      addAnexoMutation.mutate({
        editalId: editalSelecionado.id,
        anexo: novoAnexo
      });
      
      e.target.value = '';
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      alert("Erro ao fazer upload. Tente novamente.");
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleAdicionarAcao = () => {
    if (!novaAcao.trim()) return;

    const acao = {
      descricao: novaAcao,
      data_criacao: new Date().toISOString(),
      percentual_conclusao: 0
    };

    addAcaoMutation.mutate({
      editalId: editalSelecionado.id,
      acao: acao
    });
  };

  const formatDate = (dateString) => formatarDataAbertura(dateString) || 'Não informado';

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getPropostasEdital = (editalId) => {
    return (Array.isArray(propostas) ? propostas : []).filter(p => p.edital_id === editalId);
  };

  const getValorTotalPropostas = (editalId) => {
    const propostasEdital = getPropostasEdital(editalId);
    if (propostasEdital.length === 0) return null;
    
    const total = propostasEdital.reduce((sum, p) => sum + (p.valor_total_proposta || 0), 0);
    return total;
  };

  const editaisPorFase = colunasKanban.reduce((acc, fase) => {
    acc[fase.id] = (Array.isArray(editais) ? editais : []).filter(e => e.coluna_kanban === fase.id);
    return acc;
  }, {});

  const handleExportarPDF = async () => {
    if (!reportRef.current) return;
    
    setGerandoPDF(true);
    
    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (2 * margin);
      
      let currentY = margin + 18;
      let pageNumber = 1;
      
      const addPageHeader = () => {
        pdf.setFontSize(16);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Status Report - Licitações', margin, margin + 6);
        pdf.setFontSize(9);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`, margin, margin + 12);
      };
      
      addPageHeader();
      
      // Iterar pelas fases/colunas do Kanban
      for (const fase of colunasKanban) {
        const editaisFase = editaisPorFase[fase.id] || [];
        
        // Adicionar cabeçalho da fase
        pdf.setFillColor(
          fase.cor.includes('blue') ? 219 : 
          fase.cor.includes('purple') ? 233 : 
          fase.cor.includes('amber') ? 254 : 
          fase.cor.includes('red') ? 254 : 
          fase.cor.includes('green') ? 220 : 241,
          fase.cor.includes('blue') ? 234 : 
          fase.cor.includes('purple') ? 213 : 
          fase.cor.includes('amber') ? 243 : 
          fase.cor.includes('red') ? 226 : 
          fase.cor.includes('green') ? 252 : 245,
          fase.cor.includes('blue') ? 254 : 
          fase.cor.includes('purple') ? 255 : 
          fase.cor.includes('amber') ? 199 : 
          fase.cor.includes('red') ? 230 : 
          fase.cor.includes('green') ? 231 : 249
        );
        
        pdf.rect(margin, currentY, contentWidth, 12, 'F');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text(fase.titulo, margin + 5, currentY + 8);
        pdf.setFontSize(9);
        pdf.text(`${editaisFase.length} ${editaisFase.length === 1 ? 'edital' : 'editais'}`, pageWidth - margin - 20, currentY + 8);
        
        currentY += 15;
        
        if (editaisFase.length === 0) {
          pdf.setFontSize(10);
          pdf.setTextColor(100, 116, 139);
          pdf.text('Nenhum edital nesta fase', margin + 5, currentY + 5);
          currentY += 15;
        } else {
          // Processar cada edital
          for (const edital of editaisFase) {
            const valorProposta = getValorTotalPropostas(edital.id);
            const numAcoes = edital.acoes?.length || 0;
            const numAcompanhamentos = edital.acompanhamentos?.length || 0;
            
            // Buscar data de retorno
            const dataRetorno = edital.data_retorno || edital.hora_retorno;
            
            // Estimar altura necessária baseada no conteúdo
            const tituloEstimado = (edital.titulo_licitacao || edital.instituicao || edital.nome_arquivo).length > 50 ? 15 : 10;
            const acoesEstimado = numAcoes > 0 ? (8 + (Math.min(numAcoes, 3) * 4) + 2) : 0;
            const acompEstimado = numAcompanhamentos > 0 ? (8 + (Math.min(numAcompanhamentos, 3) * 4) + 2) : 0;
            const estimatedHeight = 35 + tituloEstimado + acoesEstimado + acompEstimado + (dataRetorno ? 15 : 0);
            
            // Verificar quebra de página
            if (currentY + estimatedHeight > pageHeight - margin) {
              pdf.addPage();
              pageNumber++;
              addPageHeader();
              currentY = margin + 20;
            }
            
            // Desenhar card do edital
            pdf.setDrawColor(226, 232, 240);
            pdf.setLineWidth(0.5);
            pdf.rect(margin, currentY, contentWidth, estimatedHeight);
            
            // Título
            pdf.setFontSize(11);
            pdf.setTextColor(15, 23, 42);
            const titulo = edital.titulo_licitacao || edital.instituicao || edital.nome_arquivo;
            const tituloLines = pdf.splitTextToSize(titulo, contentWidth / 2 - 10);
            pdf.text(tituloLines, margin + 3, currentY + 6);
            
            let yOffset = currentY + 6 + (tituloLines.length * 5);
            
            // Informações
            pdf.setFontSize(9);
            pdf.setTextColor(71, 85, 105);
            
            if (edital.numero_pregao && edital.numero_pregao !== 'Não localizado') {
              pdf.text(`Pregão: ${edital.numero_pregao}`, margin + 3, yOffset);
              yOffset += 5;
            }
            
            if (edital.numero_processo && edital.numero_processo !== 'Não localizado') {
              pdf.text(`Processo: ${edital.numero_processo}`, margin + 3, yOffset);
              yOffset += 5;
            }
            
            if (edital.data_abertura && edital.data_abertura !== 'Não localizado') {
              pdf.text(`Abertura: ${formatDate(edital.data_abertura)}`, margin + 3, yOffset);
              if (edital.hora_abertura && edital.hora_abertura !== 'Não localizado') {
                pdf.text(edital.hora_abertura, margin + 40, yOffset);
              }
              yOffset += 5;
            }
            
            if (edital.valor_estimado > 0) {
              pdf.setTextColor(29, 78, 216);
              pdf.text(`Valor Estimado: ${formatCurrency(edital.valor_estimado)}`, margin + 3, yOffset);
              yOffset += 5;
            }
            
            if (valorProposta > 0) {
              pdf.setTextColor(21, 128, 61);
              pdf.text(`Valor Proposta: ${formatCurrency(valorProposta)}`, margin + 3, yOffset);
              yOffset += 5;
            }
            
            // Ações e Acompanhamentos (lado direito)
            let rightY = currentY + 6;
            const rightX = margin + contentWidth / 2 + 5;
            
            if (numAcoes > 0) {
              const acoesList = edital.acoes.slice(0, 3).map(a => a.descricao);
              const acoesHeight = 8 + (acoesList.length * 4);
              
              pdf.setFillColor(239, 246, 255);
              pdf.rect(rightX, rightY, contentWidth / 2 - 10, acoesHeight, 'F');
              pdf.setFontSize(8);
              pdf.setTextColor(30, 64, 175);
              pdf.setFont(undefined, 'bold');
              pdf.text(`Ações (${numAcoes})`, rightX + 2, rightY + 4);
              pdf.setFont(undefined, 'normal');
              pdf.setFontSize(7);
              pdf.setTextColor(51, 65, 85);
              
              let acaoY = rightY + 7;
              acoesList.forEach(acao => {
                const acaoText = pdf.splitTextToSize(`• ${acao}`, contentWidth / 2 - 14);
                pdf.text(acaoText[0] + (acaoText.length > 1 ? '...' : ''), rightX + 2, acaoY);
                acaoY += 4;
              });
              
              rightY += acoesHeight + 2;
            }
            
            if (numAcompanhamentos > 0) {
              const acompList = edital.acompanhamentos.slice(0, 3).map(a => a.descricao);
              const acompHeight = 8 + (acompList.length * 4);
              
              pdf.setFillColor(240, 253, 244);
              pdf.rect(rightX, rightY, contentWidth / 2 - 10, acompHeight, 'F');
              pdf.setFontSize(8);
              pdf.setTextColor(22, 101, 52);
              pdf.setFont(undefined, 'bold');
              pdf.text(`Acompanhamentos (${numAcompanhamentos})`, rightX + 2, rightY + 4);
              pdf.setFont(undefined, 'normal');
              pdf.setFontSize(7);
              pdf.setTextColor(51, 65, 85);
              
              let acompY = rightY + 7;
              acompList.forEach(acomp => {
                const acompText = pdf.splitTextToSize(`• ${acomp}`, contentWidth / 2 - 14);
                pdf.text(acompText[0] + (acompText.length > 1 ? '...' : ''), rightX + 2, acompY);
                acompY += 4;
              });
              
              rightY += acompHeight + 2;
            }
            
            // Data de Retorno em destaque
            if (dataRetorno) {
              pdf.setFillColor(254, 226, 226);
              pdf.setDrawColor(239, 68, 68);
              pdf.setLineWidth(1.5);
              pdf.rect(rightX, rightY, contentWidth / 2 - 10, 12, 'FD');
              pdf.setFontSize(9);
              pdf.setTextColor(153, 27, 27);
              pdf.setFont(undefined, 'bold');
              let retornoText = 'RETORNO: ';
              if (edital.data_retorno) {
                retornoText += formatDate(edital.data_retorno);
              }
              if (edital.hora_retorno) {
                retornoText += ' as ' + edital.hora_retorno;
              }
              pdf.text(retornoText, rightX + 2, rightY + 7);
              pdf.setFont(undefined, 'normal');
              rightY += 14;
            }
            
            currentY += estimatedHeight + 3;
          }
        }
        
        currentY += 5;
      }
      
      pdf.save(`status-report-${format(new Date(), 'dd-MM-yyyy')}.pdf`);
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setGerandoPDF(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Erro ao carregar dados. Por favor, recarregue a página.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8" data-tour="tour-StatusReport-root">
        <div className="max-w-7xl mx-auto" ref={reportRef}>
          <div className="mb-8 flex justify-between items-start">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Status Report</h1>
              <p className="text-slate-600">Visão geral dos editais por fase do processo</p>
            </div>
            <Button
              onClick={handleExportarPDF}
              className="bg-blue-600 hover:bg-blue-700 no-print"
              disabled={gerandoPDF}
              data-tour="tour-StatusReport-primary"
            >
              {gerandoPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4 mr-2" />
                  Exportar PDF
                </>
              )}
            </Button>
          </div>

        <div className="space-y-8">
          {colunasKanban.map((fase, index) => {
            const editaisFase = editaisPorFase[fase.id] || [];
            
            return (
              <Card key={fase.id} className="border-none shadow-xl bg-white/90 backdrop-blur-sm status-section">
                <CardHeader className={`border-b ${fase.cor}`} data-fase-id={fase.id}>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {fase.titulo}
                    </CardTitle>
                    <Badge variant="outline" className={`${fase.cor} border`}>
                      {editaisFase.length} {editaisFase.length === 1 ? 'edital' : 'editais'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : editaisFase.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-slate-500">Nenhum edital nesta fase</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {editaisFase.map((edital) => {
                        const valorProposta = getValorTotalPropostas(edital.id);
                        const numPropostas = getPropostasEdital(edital.id).length;
                        const numAnexos = contarAnexosAreaEdital(edital);
                        const numAcoes = edital.acoes?.length || 0;
                        const numAcompanhamentos = edital.acompanhamentos?.length || 0;
                        
                        return (
                          <Card key={edital.id} className="border border-slate-200 hover:shadow-md transition-shadow edital-card">
                            <CardContent className="p-6">
                              <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                  <div>
                                    <h3 className="font-semibold text-lg text-slate-900 mb-1">
                                      {edital.titulo_licitacao || edital.instituicao || edital.nome_arquivo}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {edital.fase_processo && (
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                          {edital.fase_processo}
                                        </Badge>
                                      )}
                                      {edital.classificacao && edital.classificacao !== "0" && edital.classificacao !== 0 && (
                                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                          {edital.classificacao}º lugar
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {edital.numero_pregao && edital.numero_pregao !== 'Não localizado' && (
                                    <div className="flex items-start gap-2">
                                      <span className="text-sm text-slate-600 font-medium">Pregão:</span>
                                      <span className="text-sm text-slate-900">{edital.numero_pregao}</span>
                                    </div>
                                  )}

                                  {edital.identificacao && edital.identificacao !== 'Não localizado' && (
                                    <div className="flex items-start gap-2">
                                      <span className="text-sm text-slate-600 font-medium">Identificação:</span>
                                      <span className="text-sm text-slate-900">{edital.identificacao}</span>
                                    </div>
                                  )}

                                  {edital.numero_processo && edital.numero_processo !== 'Não localizado' && (
                                    <div className="flex items-start gap-2">
                                      <span className="text-sm text-slate-600 font-medium">Processo:</span>
                                      <span className="text-sm text-slate-900">{edital.numero_processo}</span>
                                    </div>
                                  )}

                                  {edital.data_abertura && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Calendar className="w-4 h-4 text-slate-500" />
                                      <span className="text-slate-600">Abertura:</span>
                                      <span className="text-slate-900">
                                        {formatDate(edital.data_abertura)}
                                        {edital.hora_abertura && edital.hora_abertura !== 'Não localizado' && (
                                          <span className="ml-2 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {edital.hora_abertura}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  )}

                                  {edital.objeto && edital.objeto !== 'Não localizado' && (
                                   <div className="flex items-start gap-2">
                                     <span className="text-sm text-slate-600 font-medium">Objeto:</span>
                                     <p className="text-sm text-slate-700 line-clamp-2">{edital.objeto}</p>
                                   </div>
                                  )}

                                  {edital.valor_estimado > 0 && (
                                   <div className="flex items-center gap-2">
                                     <span className="text-sm text-slate-600 font-medium">Valor Estimado do Pregão:</span>
                                     <span className="text-lg font-bold text-blue-700">
                                       {formatCurrency(edital.valor_estimado)}
                                     </span>
                                   </div>
                                  )}

                                  {valorProposta > 0 && (
                                   <div className="flex items-center gap-2">
                                     <span className="text-sm text-slate-600 font-medium">Valor Proposta:</span>
                                     <span className="text-lg font-bold text-green-700">
                                       {formatCurrency(valorProposta)}
                                     </span>
                                     {numPropostas > 1 && (
                                       <span className="text-xs text-slate-500">
                                         ({numPropostas} propostas)
                                       </span>
                                     )}
                                   </div>
                                  )}
                                </div>

                                <div className="space-y-3">
                                  {edital.data_retorno && (
                                    <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3">
                                      <div className="flex items-center gap-2 text-sm font-bold text-red-800">
                                        <ArrowRight className="w-4 h-4" />
                                        <span>RETORNO:</span>
                                        <span>{formatDate(edital.data_retorno)}</span>
                                        {edital.hora_retorno && (
                                          <>
                                            <Clock className="w-4 h-4" />
                                            <span>{edital.hora_retorno}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {numAcoes > 0 && (
                                   <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                     <div className="flex items-center justify-between mb-2">
                                       <span className="text-sm font-semibold text-blue-900">
                                         Ações ({numAcoes})
                                       </span>
                                       <CheckCircle2 className="w-4 h-4 text-blue-600" />
                                     </div>
                                     <div className="space-y-1 max-h-32 overflow-y-auto overflow-x-hidden">
                                       {edital.acoes.map((acao, idx) => (
                                         <p key={idx} className="text-xs text-blue-800 break-words whitespace-normal pr-2">
                                           • {acao.descricao}
                                         </p>
                                       ))}
                                     </div>
                                   </div>
                                  )}

                                  {numAcompanhamentos > 0 && (
                                   <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                     <div className="flex items-center justify-between mb-2">
                                       <span className="text-sm font-semibold text-green-900">
                                         Acompanhamentos ({numAcompanhamentos})
                                       </span>
                                       <CheckCircle2 className="w-4 h-4 text-green-600" />
                                     </div>
                                     <div className="space-y-1 max-h-32 overflow-y-auto overflow-x-hidden">
                                       {edital.acompanhamentos.map((acomp, idx) => (
                                         <p key={idx} className="text-xs text-green-800 break-words whitespace-normal pr-2">
                                           • {acomp.descricao}
                                         </p>
                                       ))}
                                     </div>
                                   </div>
                                  )}

                                  <div className="flex flex-wrap gap-2 no-print">
                                   <Button
                                     variant="outline"
                                     size="sm"
                                     onClick={() => navigate(createPageUrl("Resultados") + `?id=${edital.id}`)}
                                     className="flex-1"
                                   >
                                     <Eye className="w-4 h-4 mr-2" />
                                     Ver Detalhes
                                   </Button>
                                   <Button
                                     variant="outline"
                                     size="sm"
                                     onClick={() => {
                                       setEditalSelecionado(edital);
                                       setDialogAnexoAberto(true);
                                     }}
                                     className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                   >
                                     <Paperclip className="w-4 h-4 mr-2" />
                                     Anexo{numAnexos > 0 ? ` (${numAnexos})` : ''}
                                   </Button>
                                   <Button
                                     variant="outline"
                                     size="sm"
                                     onClick={() => {
                                       setEditalSelecionado(edital);
                                       setDialogAcaoAberto(true);
                                     }}
                                     className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                   >
                                     <Plus className="w-4 h-4 mr-2" />
                                     Ação
                                   </Button>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Dialog para Adicionar Anexo */}
      <Dialog open={dialogAnexoAberto} onOpenChange={setDialogAnexoAberto}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Adicionar Anexo</DialogTitle>
            <DialogDescription>
              Adicione um anexo ao edital{' '}
              <strong>
                {editalSelecionado?.titulo_licitacao || editalSelecionado?.instituicao || editalSelecionado?.nome_arquivo}
              </strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="upload-anexo-status">Selecionar Arquivo</Label>
              <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors cursor-pointer">
                <input
                  type="file"
                  id="upload-anexo-status"
                  onChange={handleUploadAnexo}
                  disabled={uploadingAnexo}
                  className="hidden"
                />
                <label htmlFor="upload-anexo-status" className="cursor-pointer">
                  {uploadingAnexo ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                      <p className="text-sm text-slate-600">Fazendo upload...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Paperclip className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm font-medium text-slate-700">
                        Clique para selecionar arquivo
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Qualquer tipo de arquivo
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {(() => {
              const anexosRaiz = editalSelecionado ? getAnexosRaizEdital(editalSelecionado) : [];
              const pastas = editalSelecionado ? getPastasAnexosEdital(editalSelecionado) : [];
              const temAlgum =
                anexosRaiz.length > 0 ||
                pastas.some((p) => Array.isArray(p?.anexos) && p.anexos.length > 0);
              if (!temAlgum) {
                return (
                  <p className="text-sm text-slate-500">
                    Nenhum anexo na área de anexos (a mesma da aba Anexos em Resultados). Os PDFs do
                    upload inicial não entram nesta contagem.
                  </p>
                );
              }
              const linhaAnexo = (anexo, key) => (
                <div
                  key={key}
                  className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 truncate">{anexo.nome}</span>
                  </div>
                  <FileViewer fileUrl={anexo.url} fileName={anexo.nome} compact />
                </div>
              );
              return (
                <div className="space-y-4">
                  {anexosRaiz.length > 0 && (
                    <div>
                      <Label>Anexos na raiz</Label>
                      <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                        {anexosRaiz.map((anexo, idx) => linhaAnexo(anexo, `raiz-${idx}`))}
                      </div>
                    </div>
                  )}
                  {pastas.map((pasta) => {
                    const lista = Array.isArray(pasta?.anexos) ? pasta.anexos : [];
                    if (lista.length === 0) return null;
                    return (
                      <div key={pasta.id ?? pasta.nome}>
                        <Label>Pasta: {pasta.nome || 'Sem nome'}</Label>
                        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                          {lista.map((anexo, idx) =>
                            linhaAnexo(anexo, `${pasta.id ?? pasta.nome}-${idx}`)
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogAnexoAberto(false)}
              disabled={uploadingAnexo}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Adicionar Ação Simples */}
      <Dialog open={dialogAcaoAberto} onOpenChange={setDialogAcaoAberto}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Adicionar Ação Simples</DialogTitle>
            <DialogDescription>
              Adicione uma ação rápida ao edital{' '}
              <strong>
                {editalSelecionado?.titulo_licitacao || editalSelecionado?.instituicao || editalSelecionado?.nome_arquivo}
              </strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="nova-acao">Descrição da Ação</Label>
              <Textarea
                id="nova-acao"
                placeholder="Descreva a ação a ser realizada..."
                value={novaAcao}
                onChange={(e) => setNovaAcao(e.target.value)}
                className="mt-2 min-h-[120px] resize-y"
                rows={6}
              />
            </div>

            {editalSelecionado?.acoes && editalSelecionado.acoes.length > 0 && (
              <div>
                <Label>Ações Existentes</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {editalSelecionado.acoes.slice(0, 5).map((acao, idx) => (
                    <div key={idx} className="p-2 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm text-blue-900 whitespace-pre-wrap break-words">{acao.descricao}</p>
                      {acao.data_criacao && (
                        <p className="text-xs text-blue-600 mt-1">
                          {format(new Date(acao.data_criacao), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </p>
                      )}
                    </div>
                  ))}
                  {editalSelecionado.acoes.length > 5 && (
                    <p className="text-xs text-slate-500 text-center">
                      + {editalSelecionado.acoes.length - 5} mais...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialogAcaoAberto(false);
                setNovaAcao("");
              }}
              disabled={addAcaoMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAdicionarAcao}
              disabled={!novaAcao.trim() || addAcaoMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {addAcaoMutation.isPending ? 'Adicionando...' : 'Adicionar Ação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}